// Ported from Octo.Mobile@79a7c69 src/utils/dateValue.ts
import type { SystemObjectField } from '@/shared/types/system';

/** Alanın tarih türü: yalnızca tarih, tarih+saat ya da yalnızca saat */
export type DateKind = 'date' | 'datetime' | 'time';

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * Alanın tarih türü — web EditorTypeResolver + FieldDbType:
 * DateEdit → date; TimeEdit ya da time kolonu → time; DateTimeEdit / datetime* → datetime.
 */
export function dateKindOf(
  field: Pick<SystemObjectField, 'editorTypeName' | 'editorType' | 'fieldDbTypeName' | 'fieldDbType'>,
): DateKind {
  const editor = (field.editorType?.editorTypeName || field.editorTypeName || '').toLowerCase();
  const db = (
    field.fieldDbType?.baseType ||
    field.fieldDbType?.dbTypeName ||
    field.fieldDbTypeName ||
    ''
  ).toLowerCase();
  if (editor === 'timeedit' || db === 'time') return 'time';
  if (editor === 'dateedit') return 'date';
  if (editor === 'datetimeedit') return 'datetime';
  if (db === 'date') return 'date';
  return 'datetime';
}

/**
 * Sunucu/istemci tarih değerini yerel Date'e çevirir.
 * - "YYYY-MM-DD" → yerel gece yarısı (JS'nin UTC yorumuna bırakılmaz; batı dilimlerinde gün kayardı)
 * - "YYYY-MM-DDTHH:mm[:ss]" (dilim eki yok) → yerel
 * - "...Z" / "+03:00" → JS çözer
 * - "HH:mm[:ss]" → bugün, yerel
 * - "dd.MM.yyyy[ HH:mm]" → yerel
 */
export function parseDateValue(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], m[6] ? +m[6] : 0);
  m = s.match(/^(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?$/);
  if (m) {
    const d = new Date();
    d.setHours(+m[1], +m[2], m[3] ? +m[3] : 0, 0);
    return d;
  }
  m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
  if (m) return new Date(+m[3], +m[2] - 1, +m[1], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0, m[6] ? +m[6] : 0);
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** "YYYY-MM-DD" (yerel) */
export function toDateString(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "YYYY-MM-DDTHH:mm:ss" (yerel, dilim eki yok — sunucu Convert.ToDateTime olduğu gibi okur) */
export function toDateTimeString(d: Date): string {
  return `${toDateString(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "HH:mm:ss" */
export function toTimeString(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** Türüne göre saklanacak metin */
export function serializeDateValue(d: Date, kind: DateKind): string {
  if (kind === 'date') return toDateString(d);
  if (kind === 'time') return toTimeString(d);
  return toDateTimeString(d);
}

export function formatDateDisplay(value: unknown): string {
  const d = parseDateValue(value);
  if (!d) return value == null ? '' : String(value);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function formatTimeDisplay(value: unknown): string {
  const d = parseDateValue(value);
  if (!d) return value == null ? '' : String(value);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDateTimeDisplay(value: unknown): string {
  const d = parseDateValue(value);
  if (!d) return value == null ? '' : String(value);
  return `${formatDateDisplay(d)} ${formatTimeDisplay(d)}`;
}

export function formatByKind(value: unknown, kind: DateKind): string {
  if (kind === 'date') return formatDateDisplay(value);
  if (kind === 'time') return formatTimeDisplay(value);
  return formatDateTimeDisplay(value);
}
